import React, { useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Numbers, Phone } from "@mui/icons-material";
import { Box, FormControl, Stack, Typography, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";
import { MdAddCircleOutline } from "react-icons/md";

import { closeModal, closeModalWithUpdate } from "../../../features/modal/ModalSlice";
import CustomButton from "../../Button/CustomButton";
import CustomInputBox from "../../Form/CustomInputBox";

const AddRequest = () => {
  const [formData, setFormData] = useState({
    user_id: "",
    company_id: "",
    no_of_phones_required: "",
    extension_start_range: "",
    extension_end_range: "",
    comment: "",
    request_status: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo.user);

  const handleClosePopup = () => {
    dispatch(closeModal());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const validate = () => {
    let tempErrors = {};
    if (parseInt(formData.no_of_phones_required) > 2) {
      tempErrors.no_of_phones_required = "You can request maximum up to 2 numbers";
    }
    if (parseInt(formData.extension_start_range) <= 100) {
      tempErrors.extension_start_range = "Extension start range must be greater than 100";
    }
    if (parseInt(formData.extension_start_range) >= parseInt(formData.extension_end_range)) {
      tempErrors.extension_range = "Extension start range must be smaller than extension end range";
    }
    if (
      parseInt(formData.extension_end_range) - parseInt(formData.extension_start_range) + 1 >
      10
    ) {
      tempErrors.extension_range = "You can request a maximum of 10 extensions";
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        owner_id: userInfo?.id,
        company_id: userInfo?.company_id,
        company_domain: userInfo?.company_domain,
        no_of_phones_required: parseInt(formData?.no_of_phones_required),
        extension_end_range: parseInt(formData?.extension_end_range),
        extension_start_range: parseInt(formData?.extension_start_range),
        comment: formData?.comment,
        no_extensions_required: formData.extension_end_range - formData.extension_start_range + 1,
        assigned_phone_details: [],
        request_status: "pending",
        request_from: "VOIP",
      };
      await apiClient.post("/requests", payload);

      handleClosePopup();
      toast.success("Requested Send!");
      dispatch(
        closeModalWithUpdate({
          isUpdated: {
            isUpdatedIn: "NEW_REQUEST",
            data: null,
          },
        })
      );
      navigate("/requests");
    } catch (error) {
      toast.error("Oops! Something went wrong please try again");
      console.error("Error saving request:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={"rightModal taskModal"}>
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <MdAddCircleOutline style={{ color: "white", fontSize: 24 }} />
          <Typography variant="h5" sx={{ color: "white", fontWeight: 600, m: 0 }}>
            Add Request
          </Typography>
        </Box>
        <button
          className="btnClose"
          onClick={handleClosePopup}
          style={{
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
        >
          <IoCloseOutline style={{ color: "white", fontSize: 20 }} />
        </button>
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          Request phone numbers and extensions for your team. You can request up to 2 phone numbers and a maximum of 10 extensions.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <FormControl>
              <CustomInputBox
                icon={Phone}
                placeholder="Number of Phones Required"
                label="Numbers"
                name="no_of_phones_required"
                value={formData?.no_of_phones_required}
                onChange={handleChange}
                backgroundColor={"#FFFFFF"}
                error={errors?.no_of_phones_required}
                helperText={
                  errors?.no_of_phones_required || "*You can request maximum up to 2 numbers"
                }
              />
            </FormControl>

            <FormControl>
              <CustomInputBox
                icon={Numbers}
                type="number"
                placeholder="Extension Starting Range i.e 10000"
                label="Extension start"
                name="extension_start_range"
                value={formData?.extension_start_range}
                onChange={handleChange}
                backgroundColor={"#FFFFFF"}
                error={errors?.extension_start_range}
                helperText={errors?.extension_start_range}
              />
            </FormControl>

            <FormControl>
              <CustomInputBox
                icon={Numbers}
                placeholder="Extension Ending Range i.e 10005"
                label="Extension End"
                name="extension_end_range"
                value={formData?.extension_end_range}
                onChange={handleChange}
                backgroundColor={"#FFFFFF"}
                error={errors?.extension_range}
                helperText={
                  errors?.extension_range || "*You can request maximum up to 10 extensions"
                }
              />
            </FormControl>

            <FormControl>
              <CustomInputBox
                placeholder="Add Comment here (optional)"
                name="comment"
                value={formData?.comment}
                onChange={handleChange}
                backgroundColor={"#FFFFFF"}
                multiline
                rows={4}
              />
            </FormControl>

            {/* Info Box */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#F0F9FF",
                borderRadius: "8px",
                border: "1px solid #BAE6FD",
                display: "flex",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: "#0EA5E9",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                i
              </Box>
              <Typography variant="body2" sx={{ color: "#0369A1", fontSize: "0.875rem", lineHeight: 1.6 }}>
                Each extension is mapped to a phone number. Your request will be reviewed by the admin and processed accordingly.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      <Divider />

      {/* Footer */}
      <Box className="d-flex rightModalFooterBtn" sx={{ borderTop: "1px solid #E5E7EB" }}>
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleClosePopup}
          disabled={loading}
          sx={{
            minWidth: "40%",
            borderRadius: "0px",
            backgroundColor: "#FFFFFF",
            color: "#333",
            padding: "14px 16px",
            fontWeight: 500,
            fontSize: "0.938rem",
            boxShadow: "none",
            borderRight: "1px solid #E5E7EB",
            "&:hover": {
              backgroundColor: "#F9FAFB",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#F3F4F6",
              color: "#9CA3AF",
            },
          }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          loading={loading}
          sx={{
            minWidth: "60%",
            borderRadius: "0px",
            backgroundColor: "#667eea",
            color: "#FFFFFF",
            padding: "14px 16px",
            fontWeight: 600,
            fontSize: "0.938rem",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#5568d3",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          Submit Request
        </CustomButton>
      </Box>
    </div>
  );
};

export default AddRequest;
